import React, { useState, useEffect } from 'react';
import Modal from '../components/Modal';
import ImageCropper from '../components/ImageCropper';
import '../App.css';
import { firestore, storage, firestoreAsatizah } from '../utils/FirebaseConfig';
import { collection, setDoc, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Asatizah = ({ isMobile }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [asatizahData, setAsatizahData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ nama: '', jawatan: '', image: '' });
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [mosqueName, setMosqueName] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterSource, setFilterSource] = useState('all');

  // ✅ Guna userProfile dari localStorage (bukan field tunggal)
  const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
  const mosqueId = userProfile?.mosqueId || '';

  useEffect(() => {
    if (!mosqueId) {
      console.warn('⚠️ Tiada mosqueId dalam userProfile, sila login semula.');
      return;
    }

    let adminData = [];
    let selfData = [];

    const updateCombinedData = () => {
      const combined = [...adminData, ...selfData];
      combined.sort((a, b) =>
        String(a.nama || '').toLowerCase().localeCompare(String(b.nama || '').toLowerCase())
      );
      setAsatizahData(combined);
    };

    // Kedua-dua listener mesti diwujudkan sekali sahaja. Sebelum ini listener
    // "self" dicipta semula setiap kali koleksi admin berubah dan tidak ditutup.
    const unsubAdmin = onSnapshot(
      collection(firestore, 'asatizah'),
      (adminSnap) => {
        adminData = adminSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          source: 'admin',
        }));
        updateCombinedData();
      },
      (err) => console.error('❌ Gagal memuatkan asatizah admin:', err)
    );

    const unsubSelf = onSnapshot(
      collection(firestoreAsatizah, 'asatizah'),
      (selfSnap) => {
        selfData = selfSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          source: 'self',
        }));
        updateCombinedData();
      },
      (err) => console.error('❌ Gagal memuatkan akaun asatizah:', err)
    );

    return () => {
      unsubAdmin();
      unsubSelf();
    };
  }, [mosqueId]);

  useEffect(() => {
    const fetchMosqueName = async () => {
      if (!mosqueId) return;

      try {
        const docRef = doc(firestore, 'masajid', mosqueId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const prof = docSnap.data()?.profail;
          setMosqueName(prof?.nama || userProfile?.namaMasjid || 'Masjid Tidak Dikenal');
        }
      } catch (err) {
        console.error('❌ Gagal ambil nama masjid:', err);
      }
    };

    fetchMosqueName();
  }, [mosqueId, userProfile?.namaMasjid]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleCropComplete = (blob) => setCroppedImageBlob(blob);

  const generateUniqueId = async (baseId) => {
    let uniqueId = baseId;
    let count = 1;
    while (true) {
      const refDoc = doc(firestore, 'asatizah', uniqueId);
      const snap = await getDoc(refDoc);
      if (!snap.exists()) break;
      uniqueId = `${baseId}-${count++}`;
    }
    return uniqueId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUploading) return;
    if (!formData.nama || !formData.jawatan || !croppedImageBlob) {
      toast.error('Sila isi semua medan dan crop gambar!');
      return;
    }

    setIsUploading(true);
    try {
      const storageRef = ref(storage, `asatizahImages/${Date.now()}`);
      await uploadBytes(storageRef, croppedImageBlob);
      const imageUrl = await getDownloadURL(storageRef);

      const baseId = formData.nama.trim().replace(/\s+/g, '_').toLowerCase();
      const uniqueId = await generateUniqueId(baseId);

      const dataToSave = {
        ...formData,
        image: imageUrl,
        create_by: mosqueName || 'Unknown Mosque',
        createdAt: new Date(),
      };

      await setDoc(doc(firestore, 'asatizah', uniqueId), dataToSave);
      setAsatizahData((prev) => [...prev, dataToSave]);
      setFormData({ nama: '', jawatan: '', image: '' });
      setImageSrc(null);
      setCroppedImageBlob(null);
      setIsModalOpen(false);
      toast.success('Asatizah berjaya ditambah!');
    } catch (err) {
      console.error('❌ Ralat tambah asatizah:', err);
      toast.error('Gagal menambah data.');
    } finally {
      setIsUploading(false);
    }
  };

  const closeModal = () => setIsModalOpen(false);
  const openModal = () => {
    setFormData({ nama: '', jawatan: '', image: '' });
    setImageSrc(null);
    setCroppedImageBlob(null);
    setIsModalOpen(true);
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleCardClick = (person) => {
    setSelectedPerson(person);
    setShowDetailModal(true);
  };

  const filteredAsatizah = asatizahData.filter((person) => {
    const matchesSearch = person.nama?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterSource === 'all' ||
      (filterSource === 'self' && person.source === 'self') ||
      (filterSource === 'admin' && person.source === 'admin');
    return matchesSearch && matchesFilter;
  });

  const renderFilterExplanation = () => {
    if (filterSource === 'self') {
      return (
        <div className="filter-explanation">
          <p>
            Profail ini dicipta sendiri oleh asatizah melalui pautan{' '}
            <a href="https://asatizah.etikaf.com" target="_blank" rel="noopener noreferrer">
              asatizah.etikaf.com
            </a>.
            Setiap asatizah boleh mengurus dan mengemaskini profail dan jadual mereka sendiri.
            <br />
            <br />
            <b>Kelebihan:</b> Maklumat lebih terkini dan tepat, serta boleh diubahsuai sendiri oleh pemilik.
          </p>
        </div>
      );
    }

    if (filterSource === 'admin') {
      return (
        <div className="filter-explanation">
          <p>
            Profail ini dicipta oleh pihak masjid berdasarkan rekod jemputan dan program terdahulu.
            Maklumat ini boleh dikongsi dan dirujuk oleh semua masjid yang menggunakan sistem ini.
            <br />
            <br />
            <b>Cara:</b> Klik butang tambah di atas untuk menambah asatizah ke dalam sistem.
          </p>
        </div>
      );
    }

    return null;
  };

  if (!mosqueId) {
    return (
      <div style={{ padding: 20 }}>
        ⚠️ ID masjid tidak ditemui. Sila log masuk semula.
      </div>
    );
  }

  return (
    <div>

      <div className="search-bar" style={{ display: 'flex', alignItems: 'center', marginRight: isMobile ? '0' : '20px' }}>
        <input
          type="text"
          placeholder="Cari nama asatizah..."
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ padding: '10px', flex: 1, marginRight: '10px' }}
        />
        <button className="button" onClick={openModal}>
          Tambah
        </button>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '10px' }}>
        <button className={`filter-button ${filterSource === 'all' ? 'active' : ''}`} onClick={() => setFilterSource('all')}>
          Semua
        </button>
        <button className={`filter-button ${filterSource === 'self' ? 'active' : ''}`} onClick={() => setFilterSource('self')}>
          Dicipta Sendiri
        </button>
        <button className={`filter-button ${filterSource === 'admin' ? 'active' : ''}`} onClick={() => setFilterSource('admin')}>
          Dicipta Masjid
        </button>
      </div>

      <Modal isOpen={isModalOpen} closeModal={closeModal}>
        <form onSubmit={handleSubmit} style={{ display: 'flex' }}>
          <div style={{ width: '300px' }}>
            <ImageCropper imageSrc={imageSrc} onCropComplete={handleCropComplete} />
            <input type="file" name="image" onChange={handleFileChange} />
          </div>
          <div style={{ marginLeft: '10px', width: '100%' }}>
            <label style={{ fontSize: '14px', marginBottom: '5px', marginTop: '15px' }}>
              Nama :
              <input
                className="input-group"
                type="text"
                name="nama"
                value={formData.nama}
                onChange={handleInputChange}
                style={{ width: '100%', height: '50px', fontSize: '18px', padding: '10px', marginBottom: '10px' }}
              />
            </label>
            <label style={{ fontSize: '14px', marginBottom: '5px' }}>
              Jawatan / Pengenalan :
              <input
                className="input-group"
                type="text"
                name="jawatan"
                value={formData.jawatan}
                onChange={handleInputChange}
                style={{ width: '100%', height: '50px', fontSize: '18px', padding: '10px' }}
              />
            </label>
            <button className="button" type="submit" style={{ marginTop: '30px' }} disabled={isUploading || !mosqueName}>
              {isUploading ? 'Uploading...' : 'Simpan'}
            </button>
            <span style={{ marginLeft: '20px' }} onClick={closeModal}>
              Kembali
            </span>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showDetailModal} closeModal={() => setShowDetailModal(false)}>
        {selectedPerson && (
          <div style={{ textAlign: 'center', maxWidth: 400, padding: 18 }}>
            <img
              src={selectedPerson.image}
              alt="Avatar Besar"
              style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                objectFit: 'cover',
                marginBottom: 18,
                border: '2px solid #7A1777',
              }}
            />
            <h2 style={{ marginBottom: 4 }}>{selectedPerson.nama}</h2>
            <h4 style={{ color: '#7A1777', marginBottom: 8 }}>{selectedPerson.jawatan}</h4>
            {selectedPerson.phone && (
              <div style={{ fontSize: 14, marginBottom: 6 }}>
                <b>Telefon:</b> {selectedPerson.phone}
              </div>
            )}
            <div style={{ color: '#ccc', fontSize: 12, marginTop: 18 }}>
              Dicipta oleh: <b>{selectedPerson.create_by}</b>
            </div>
          </div>
        )}
      </Modal>

      {renderFilterExplanation()}

      <div className="person-grid">
        {filteredAsatizah.map((person, index) => (
          <div
            key={index}
            className="person-card"
            style={{
              cursor: person.source === 'self' ? 'pointer' : 'default',
              opacity: person.source === 'self' ? 1 : 0.85,
            }}
            onClick={() => {
              if (person.source === 'self') handleCardClick(person);
            }}
          >
            <img
              src={person.image}
              alt="Avatar"
              style={{
                border: person.source === 'self' ? '2px solid #7A1777' : '2px solid #fff',
              }}
            />
            <div className="person-details">
              <h5>{person.nama}</h5>
              <p>{person.jawatan}</p>
              <p style={{ fontSize: '10px', color: 'lightgray' }}>Created by {person.create_by}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Asatizah;
